<template>
  <div v-if="error" class="flex min-h-[40vh] items-center justify-center p-6">
    <div class="w-full max-w-md rounded-2xl border border-error-200 bg-white p-6 text-center dark:border-error-500/30 dark:bg-gray-900">
      <div class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-error-50 text-error-500 dark:bg-error-500/10">
        <AlertTriangle class="h-6 w-6" />
      </div>
      <h2 class="mb-1 text-base font-semibold text-gray-900 dark:text-white">این بخش با خطا مواجه شد</h2>
      <p class="prose-fa mx-auto mb-4 text-sm text-gray-500 dark:text-gray-400">
        خطا ثبت شد و تیمِ فنی آن را می‌بیند. می‌توانید دوباره تلاش کنید یا صفحه را دوباره بارگذاری کنید.
      </p>
      <div class="flex justify-center gap-2">
        <button type="button" class="inline-flex h-9 items-center justify-center rounded-lg bg-brand-action px-4 text-sm font-medium text-white transition hover:bg-brand-action-hover" @click="reset">
          تلاشِ دوباره برای همین بخش
        </button>
        <button type="button" class="inline-flex h-9 items-center justify-center rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-white/5" @click="reload">
          بارگذاریِ دوبارهٔ صفحه
        </button>
      </div>
      <button type="button" class="mt-3 inline-flex h-8 items-center justify-center rounded-lg px-3 text-xs font-medium text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800" @click="showDetail = !showDetail">
        {{ showDetail ? 'پنهان‌کردنِ جزئیاتِ فنی' : 'نمایشِ جزئیاتِ فنی' }}
      </button>
      <!-- R-C6: the one thing on this card that is legitimately Latin — a JS error message the
           operator may have to paste to someone — so it gets `dir="ltr"` + `font-mono`, which is the
           treatment the rule reserves for identifiers a user must copy verbatim. `text-[11px]` was
           one of the 231 arbitrary sizes and below the 12px floor; `text-xs` is the nearest step.
           `text-right` -> `text-left` is the one visible change: this box is `dir="ltr"`, so the
           physical class was pinning an English stack message to the far edge of its own box. -->
      <pre v-if="showDetail" class="mt-2 max-h-40 overflow-auto rounded-lg bg-gray-100 p-3 text-left font-mono text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300" dir="ltr">{{ error.message }}</pre>
    </div>
  </div>
  <slot v-else />
</template>

<script setup>
import { ref, onErrorCaptured, watch } from 'vue'
import { useRoute } from 'vue-router'
import { AlertTriangle } from 'lucide-vue-next'
import { reportClientError } from '@/services/errorReporter'

const error = ref(null)
const showDetail = ref(false)
const route = useRoute()

// Catch any error thrown while rendering / in a lifecycle hook of a child, show the fallback, and report it.
onErrorCaptured((err, _instance, info) => {
  error.value = err
  reportClientError(err, { source: 'boundary', info })
  return false // handled — don't propagate to the global handler as an unhandled crash
})

// Navigating away from a broken page should clear the boundary so the next page renders normally.
watch(() => route.fullPath, () => { error.value = null; showDetail.value = false })

const reset = () => { error.value = null; showDetail.value = false }
const reload = () => window.location.reload()
</script>
